import React, { Children, useState, cloneElement } from 'react';
import { Portal } from '@headlessui/react';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import { isReactComponent } from '../../utils/component/component.esm.js';
import { usePopper } from '../../utils/usePopper.esm.js';
import { SINGLELINE_PLACEMENTS, MULTILINE_PLACEMENTS, POPOVER_PLACEMENTS } from '../../utils/constants.esm.js';

function Tooltip({ children, dataMetrics, dataTestId, label, singleLine = false, addClassName, defaultPlacement = POPOVER_PLACEMENTS.BOTTOM_END, }) {
    const child = Children.only(children);
    const [placement, setPlacement] = useState(defaultPlacement);
    const [isHover, setIsHover] = useState(false);
    const singleLinePlacements = Object.values(SINGLELINE_PLACEMENTS);
    const multiLinePlacements = Object.values(MULTILINE_PLACEMENTS);
    const caretTooltipOffset = 17;
    const { triggerRef, containerRef } = usePopper({
        placement: defaultPlacement,
        strategy: "fixed",
        modifiers: [
            {
                name: "offset",
                options: {
                    offset: ({ placement, reference, }) => {
                        if (placement === POPOVER_PLACEMENTS.BOTTOM_START ||
                            placement === POPOVER_PLACEMENTS.TOP_START) {
                            return [reference.width / 2 - caretTooltipOffset, 0];
                        }
                        else if (placement === POPOVER_PLACEMENTS.BOTTOM_END ||
                            placement === POPOVER_PLACEMENTS.TOP_END) {
                            return [-(reference.width / 2 - caretTooltipOffset), 0];
                        }
                        else if (placement === POPOVER_PLACEMENTS.LEFT_START ||
                            placement === POPOVER_PLACEMENTS.RIGHT_START) {
                            return [reference.height / 2 - caretTooltipOffset, 0];
                        }
                        else if (placement === POPOVER_PLACEMENTS.LEFT_END ||
                            placement === POPOVER_PLACEMENTS.RIGHT_END) {
                            return [-(reference.height / 2 - caretTooltipOffset), 0];
                        }
                        else {
                            return [];
                        }
                    },
                },
            },
            {
                name: "getPlacement",
                enabled: true,
                phase: "afterWrite",
                fn({ state }) {
                    setPlacement(state.placement);
                },
            },
            {
                name: "flip",
                options: {
                    fallbackPlacements: singleLine
                        ? singleLinePlacements
                        : multiLinePlacements,
                },
            },
        ],
    });
    let defaultStyle = "tw-relative tw-z-50 before:tw-content-[''] before:tw-absolute before:tw-h-0 before:tw-w-0 before:tw-border-solid before:tw-border-transparent before:tw-border-8 after:tw-content-[''] after:tw-absolute after:tw-h-0 after:tw-w-0 after:tw-border-solid after:tw-border-transparent after:tw-border-8";
    if (placement === POPOVER_PLACEMENTS.BOTTOM_END) {
        defaultStyle = classNames(defaultStyle, "tw-top-3 before:tw-right-2 before:tw--top-2 before:tw-border-t-0 before:tw-border-b-gray-800 after:tw-right-2 after:tw-border-t-0 after:tw--top-1.5", "after:tw-border-b-gray-900");
    }
    else if (placement === POPOVER_PLACEMENTS.BOTTOM_START) {
        defaultStyle = classNames(defaultStyle, "tw-top-3 before:tw-left-2 before:tw--top-2 before:tw-border-t-0 before:tw-border-b-gray-800 after:tw-left-2 after:tw-border-t-0 after:tw--top-1.5", "after:tw-border-b-gray-900");
    }
    else if (placement === POPOVER_PLACEMENTS.TOP_START) {
        defaultStyle = classNames(defaultStyle, "tw-bottom-3 before:tw-left-2 before:tw--bottom-2 before:tw-border-b-0 before:tw-border-t-gray-800 after:tw-left-2 after:tw-border-b-0 after:tw--bottom-1.5 ", "after:tw-border-t-gray-900");
    }
    else if (placement === POPOVER_PLACEMENTS.TOP_END) {
        defaultStyle = classNames(defaultStyle, "tw-bottom-3 before:tw-right-2 before:tw--bottom-2 before:tw-border-b-0 before:tw-border-t-gray-800 after:tw-right-2 after:tw-border-b-0 after:tw--bottom-1.5", "after:tw-border-t-gray-900");
    }
    else if (placement === POPOVER_PLACEMENTS.LEFT_START) {
        defaultStyle = classNames(defaultStyle, "tw-right-3 before:tw-top-2 before:tw--right-2 before:tw-border-r-0 before:tw-border-l-gray-800 after:tw-top-2 after:tw-border-r-0 after:tw--right-1.5", "after:tw-border-l-gray-900");
    }
    else if (placement === POPOVER_PLACEMENTS.LEFT_END) {
        defaultStyle = classNames(defaultStyle, "tw-right-3 before:tw-bottom-2 before:tw--right-2 before:tw-border-r-0 before:tw-border-l-gray-800 after:tw-bottom-2 after:tw-border-r-0 after:tw--right-1.5", "after:tw-border-l-gray-900");
    }
    else if (placement === POPOVER_PLACEMENTS.RIGHT_START) {
        defaultStyle = classNames(defaultStyle, "tw-left-3 before:tw-top-2 before:tw--left-2 before:tw-border-l-0 before:tw-border-r-gray-800 after:tw-top-2 after:tw-border-l-0 after:tw--left-1.5", "after:tw-border-r-gray-900");
    }
    else if (placement === POPOVER_PLACEMENTS.RIGHT_END) {
        defaultStyle = classNames(defaultStyle, "tw-left-3 before:tw-bottom-2 before:tw--left-2 before:tw-border-l-0 before:tw-border-r-gray-800 after:tw-bottom-2 after:tw-border-l-0 after:tw--left-1.5", "after:tw-border-r-gray-900");
    }
    else if (placement === POPOVER_PLACEMENTS.BOTTOM) {
        defaultStyle = classNames(defaultStyle, "tw-top-3 before:tw-right-1/2 before:tw-translate-x-1/2 before:tw--top-2 before:tw-border-t-0 before:tw-border-b-gray-800 after:tw-right-1/2 after:tw-translate-x-1/2 after:tw-border-t-0 after:tw--top-1.5 ", "after:tw-border-b-gray-900");
    }
    else if (placement === POPOVER_PLACEMENTS.TOP) {
        defaultStyle = classNames(defaultStyle, "tw-bottom-3 before:tw-right-1/2 before:tw-translate-x-1/2 before:tw--bottom-2 before:tw-border-b-0 before:tw-border-t-gray-800 after:tw-right-1/2 after:tw-translate-x-1/2 after:tw-border-b-0 after:tw--bottom-1.5 ", "after:tw-border-t-gray-900");
    }
    else if (placement === POPOVER_PLACEMENTS.RIGHT) {
        defaultStyle = classNames(defaultStyle, "tw-left-3 before:tw-bottom-1/2 before:tw-translate-y-1/2 before:tw--left-2 before:tw-border-l-0 before:tw-border-r-gray-800 after:tw-bottom-1/2 after:tw-translate-y-1/2 after:tw-border-l-0 after:tw--left-1.5 ", "after:tw-border-r-gray-900");
    }
    else if (placement === POPOVER_PLACEMENTS.LEFT) {
        defaultStyle = classNames(defaultStyle, "tw-right-3 before:tw-bottom-1/2 before:tw-translate-y-1/2 before:tw--right-2 before:tw-border-r-0 before:tw-border-l-gray-800 after:tw-bottom-1/2 after:tw-translate-y-1/2 after:tw-border-r-0 after:tw--right-1.5 ", "after:tw-border-l-gray-900");
    }
    const className = classNames(addClassName, "tw-bg-gray-900 tw-text-dark-bg tw-border tw-border-gray-800", "tw-font-lato tw-text-xs tw-p-2 tw-rounded tw-shadow-popover dark:tw-shadow-dark-popover tw-border-solid tw-max-w-[230px]", !singleLine && "tw-break-words", defaultStyle);
    return (React.createElement(React.Fragment, null,
        isReactComponent(child.type) ? (React.createElement("div", { ref: triggerRef, className: " tw-flex tw-items-center" }, cloneElement(child, {
            onMouseEnter: () => setIsHover(true),
            onMouseLeave: () => setIsHover(false),
            onFocus: () => setIsHover(true),
            onBlur: () => setIsHover(false),
        }))) : (cloneElement(child, {
            onMouseEnter: () => setIsHover(true),
            onMouseLeave: () => setIsHover(false),
            onFocus: () => setIsHover(true),
            onBlur: () => setIsHover(false),
            ref: triggerRef,
        })),
        React.createElement(Portal, null,
            React.createElement("div", { "data-metrics": dataMetrics, "data-testid": dataTestId || "tooltip", ref: containerRef, role: "tooltip", className: "tw-z-50" }, isHover && React.createElement("div", { className: className }, label)))));
}

export { Tooltip };
//# sourceMappingURL=Tooltip.esm.js.map
